/* src/main.ts — the entry point.
 *
 * Phase 1 replaces Phase 0's static shell with the real thing: a 3D city streamed from
 * vector tiles. The Phase 0 primitives all survive unchanged — tokens, the root dispatcher,
 * the store, the event bus — which was the point of building them first.
 */

import './ui/tokens.css';
import './ui/base.css';
import './ui/world.css';
import './ui/game.css';

import { exposeStatsForTests, stats } from './core/stats.ts';
import { IS_DEV, RESET_ON_LOAD } from './core/env.ts';
import { emit, on, type InteriorDescriptor, type InteriorState } from './core/events.ts';
import { gameStore } from './core/store.ts';
import type { GameState, Parcel } from './core/types.ts';
import {
  buyFixture,
  buyItem,
  buyParcel,
  checkPlacement,
  clearShelf,
  devAdvanceMinutes,
  devGrantCash,
  ensureSignedIn,
  fetchParcel,
  fetchRestockQuote,
  NotConfiguredError,
  orderStock,
  placeCart,
  prepCart,
  fetchParcelAt,
  fetchParcelsForSale,
  fetchParcelByOsmId,
  removeFixture,
  resetPlayer,
  restockCart,
  sellParcel,
  sellItem,
  setCartCuisine,
  setCartPrice,
  stockShelf,
  surveyMap,
  surveyRequest,
  surveysInView,
  type SurveyStatus,
} from './net/gameApi.ts';
import { tileBoundsLonLat } from './map/tileMath.ts';
import { withinAustralia } from './core/cities.ts';
import { PollingScheduler } from './net/pollingScheduler.ts';
import { incomeInterpolator } from './sim/incomeInterpolator.ts';
import { ConnStatus } from './ui/connStatus.ts';
import { installRootDispatcher, registerAction } from './ui/dispatch.ts';
import { el } from './ui/dom.ts';
import { HudBar } from './ui/hud/hudBar.ts';
import { InteriorHud } from './ui/hud/interiorHud.ts';
import { ViewToggles } from './ui/hud/viewToggles.ts';
import { LoadingScreen } from './ui/loadingScreen.ts';
import { MarketTicker } from './ui/marketTicker.ts';
import { toast } from './ui/toast.ts';
import { openBuildingInspector } from './ui/windows/buildingInspector.ts';
import { openCartWindow } from './ui/windows/cartWindow.ts';
import { openFixturePanel } from './ui/windows/fixturePanel.ts';
import { openMarketWindow } from './ui/windows/marketWindow.ts';
import { openTravelWindow } from './ui/windows/travelWindow.ts';
import { SurveyPill } from './ui/hud/surveyPill.ts';
import { startPlacement, type PlacementFlow } from './ui/windows/placeCart.ts';
import { WindowManager } from './ui/windows/windowChrome.ts';
import { createWorldView } from './world/worldView.ts';

function attribution(): HTMLElement {
  // ODbL requires attribution to stay visible wherever OSM-derived data is shown
  // (CLAUDE.md Legal). Every building on screen is derived from OSM.
  return el(
    'div',
    { class: 'gf-attrib' },
    '© ',
    el(
      'a',
      {
        attrs: {
          href: 'https://www.openstreetmap.org/copyright',
          target: '_blank',
          rel: 'noopener noreferrer',
        },
      },
      'OpenStreetMap',
    ),
    ' contributors · ',
    el(
      'a',
      {
        attrs: {
          href: 'https://www.naturalearthdata.com/',
          target: '_blank',
          rel: 'noopener noreferrer',
        },
      },
      'Natural Earth',
    ),
    ' · ',
    el(
      'a',
      {
        attrs: {
          href: 'https://openfreemap.org/',
          target: '_blank',
          rel: 'noopener noreferrer',
        },
      },
      'OpenFreeMap',
    ),
  );
}

/** `?debug=1` perf readout. Red when a CLAUDE.md budget is breached, so it is unmissable. */
function installPerfOverlay(hud: HTMLElement): void {
  const node = el('div', { class: 'gf-perf' });
  hud.appendChild(node);

  const tick = (): void => {
    const s = stats.snapshot();
    const over = (v: number, budget: number): string =>
      `<b class="${v > budget ? 'is-over' : ''}">${v.toFixed(0)}</b>`;
    // cpu p95, not the frame gap: at a locked 60fps the gap is always ~16.7 ms and would
    // read as a permanent budget breach while telling you nothing.
    const mem = window.__gfMem?.();
    node.innerHTML =
      `fps     ${s.fps.toFixed(0)}\n` +
      `cpu p95 ${over(s.cpuMsP95, 8)} ms / 8\n` +
      `calls   ${over(s.drawCalls, 300)}    / 300\n` +
      `tris    ${(s.triangles / 1000).toFixed(0)}k   / 2500k\n` +
      `heap    ${String(mem?.heapMB ?? 0)}MB`;
    window.setTimeout(tick, 250);
  };
  tick();
}

function main(): void {
  const root = document.getElementById('gf-app');
  if (!root) throw new Error('main: #gf-app root is missing from index.html');

  installRootDispatcher(root);
  exposeStatsForTests();

  const loading = new LoadingScreen();

  /*
   * Resume where the player last was — Perth stays Perth across a reload. Cleared whenever
   * RESET_ON_LOAD wipes the run, so the fresh-start dev loop (and every e2e) still opens on
   * the spawn. Stored client-side only: a viewpoint is a camera fact, not game state.
   */
  const VIEWPOINT_KEY = 'gf-viewpoint';
  const savedViewpoint = ((): { lon: number; lat: number; distance: number } | null => {
    if (RESET_ON_LOAD) {
      localStorage.removeItem(VIEWPOINT_KEY);
      return null;
    }
    try {
      const raw = localStorage.getItem(VIEWPOINT_KEY);
      if (raw === null) return null;
      const v = JSON.parse(raw) as { lon?: unknown; lat?: unknown; distance?: unknown };
      if (typeof v.lon !== 'number' || typeof v.lat !== 'number') return null;
      return {
        lon: v.lon,
        lat: v.lat,
        distance: typeof v.distance === 'number' ? Math.min(15000, Math.max(60, v.distance)) : 900,
      };
    } catch {
      return null;
    }
  })();

  const world = createWorldView(root, savedViewpoint ?? undefined);
  window.setInterval(() => {
    localStorage.setItem(VIEWPOINT_KEY, JSON.stringify(world.currentViewpoint()));
  }, 5000);

  const hud = el('div', { class: 'gf-hud' });
  root.appendChild(hud);

  const windows = new WindowManager(root);
  registerAction('window:close', (_e, target) => {
    const id = target.dataset['windowId'];
    if (id !== undefined) windows.get(id)?.close();
  });

  new HudBar({ windows }).mount(hud);
  new ViewToggles().mount(hud);
  new MarketTicker().mount(hud);
  new SurveyPill().mount(hud);

  /*
   * The interior dashboard and its shop plumbing. Everything the player does inside a building
   * funnels through here: a purchase or a stocked shelf runs the RPC, syncs the new game state,
   * re-reads the parcel, and re-points the 3D room via `interior:refresh` — so the walls, the
   * HUD and the panel are always describing the same building.
   */
  const applyState = (state: GameState): void => {
    incomeInterpolator.sync(state);
    gameStore.set(state);
  };

  const descriptorFrom = (p: Parcel): InteriorDescriptor => ({
    parcelId: p.id,
    name: p.osmWayId === null ? 'Building' : `Building #${String(p.osmWayId)}`,
    floors: p.floors,
    reachableFloors: p.reachableFloors,
    ring: p.ring,
    lon: p.lon,
    lat: p.lat,
    osmWayId: p.osmWayId,
    frontageDeg: p.frontageDeg,
    fixtures: p.fixtures,
    customersPerMin: p.shop?.customersPerMin ?? 0,
  });

  const refreshInterior = async (parcelId: string): Promise<Parcel | null> => {
    const fresh = await fetchParcel(parcelId);
    if (fresh) emit('interior:refresh', descriptorFrom(fresh));
    return fresh;
  };

  new InteriorHud({
    onBuy: async (parcelId, kind, floor) => {
      try {
        applyState(await buyFixture(parcelId, kind, floor));
        await refreshInterior(parcelId);
        toast(kind === 'stairs' ? 'Stairs built.' : `${kind[0]?.toUpperCase() ?? ''}${kind.slice(1)} installed.`);
      } catch (err) {
        toast(err instanceof Error ? err.message : 'Could not buy that.');
      }
    },
    onInteract: (parcelId, fixtureId) => {
      void fetchParcel(parcelId).then((parcel) => {
        if (parcel) openFixturePanel(windows, root, parcel, fixtureId, fixtureHooks);
      });
    },
  }).mount(hud);

  const fixtureHooks = {
    onStock: async (fixtureId: string, itemId: string, price: number) => {
      const parcelId = currentInteriorParcel();
      applyState(await stockShelf(fixtureId, itemId, price));
      return parcelId ? refreshInterior(parcelId) : null;
    },
    onClear: async (fixtureId: string) => {
      const parcelId = currentInteriorParcel();
      applyState(await clearShelf(fixtureId));
      return parcelId ? refreshInterior(parcelId) : null;
    },
    onRemove: async (fixtureId: string) => {
      const parcelId = currentInteriorParcel();
      applyState(await removeFixture(fixtureId));
      return parcelId ? refreshInterior(parcelId) : null;
    },
    onOrder: async (parcelId: string, itemId: string, qty: number) => {
      applyState(await orderStock(parcelId, itemId, qty));
      return refreshInterior(parcelId);
    },
  };

  let interiorParcelId: string | null = null;
  on('interior:state', (s) => {
    interiorParcelId = s.active ? s.parcelId : null;
  });
  const currentInteriorParcel = (): string | null => interiorParcelId;

  // A ticker chip opens the market filtered to that commodity.
  registerAction('market:item', (_e, target) => {
    openMarketWindow(windows, target.dataset['itemId']);
  });
  registerAction('hud:market', () => {
    openMarketWindow(windows);
  });
  registerAction('hud:travel', () => {
    openTravelWindow(windows);
  });
  // Travel rows carry their destination in data attributes; one registration serves them all.
  registerAction('travel:go', (_e, target) => {
    const lon = Number(target.dataset['lon']);
    const lat = Number(target.dataset['lat']);
    const name = target.dataset['city'] ?? 'the destination';
    if (!Number.isFinite(lon) || !Number.isFinite(lat)) return;
    windows.get('travel')?.close();
    world.travelTo(lon, lat);
    toast(`Heading to ${name} — un-surveyed blocks fill in as the crew reports back.`);
  });

  /*
   * ── The rest of Australia: survey coordination ──────────────────────────────────────
   * The world emits which z14 cells the camera can see; this block owns everything network
   * about them. Cells with no server row get requested (the server itself decides between
   * "already has data → done" and "queue it for the crew"); while anything in view is queued
   * or fetching we poll, and each cell that flips to done invalidates its tiles so the new
   * blocks stream in. World data is allowed to fail (CLAUDE.md): every error here degrades
   * to "try again on the next poll", never to a broken map.
   */
  const surveyKnown = new Map<string, SurveyStatus>();
  const surveyRetried = new Set<string>();
  let surveyView: {
    lon: number;
    lat: number;
    cells: ReadonlyArray<readonly [number, number]>;
  } | null = null;
  let surveyBusy = false;
  let surveyTimer: number | null = null;
  let lastPendingShown = 0;
  // The watcher must not talk to the server before boot's sign-in lands: the RPCs would 401,
  // and a concurrent ensureSignedIn() could mint a second anonymous user. Flipped by the boot
  // chain below, which then runs the reconcile the early viewcells events skipped.
  let surveySessionReady = false;

  const surveyKey = (x: number, y: number): string => `${String(x)}/${String(y)}`;

  const scheduleSurveyPoll = (ms: number): void => {
    if (surveyTimer !== null) window.clearTimeout(surveyTimer);
    surveyTimer = window.setTimeout(() => {
      surveyTimer = null;
      void reconcileSurveys();
    }, ms);
  };

  async function reconcileSurveys(): Promise<void> {
    if (!surveySessionReady || surveyBusy || !surveyView) return;
    const { lon, lat, cells } = surveyView;
    if (cells.length === 0) return;
    surveyBusy = true;
    try {
      // One bbox query covers every visible cell.
      let minLon = Infinity;
      let minLat = Infinity;
      let maxLon = -Infinity;
      let maxLat = -Infinity;
      for (const [x, y] of cells) {
        const b = tileBoundsLonLat(14, x, y);
        minLon = Math.min(minLon, b.minLon);
        minLat = Math.min(minLat, b.minLat);
        maxLon = Math.max(maxLon, b.maxLon);
        maxLat = Math.max(maxLat, b.maxLat);
      }
      const rows = await surveysInView(minLon, minLat, maxLon, maxLat);
      const prev = new Map(surveyKnown);
      for (const r of rows) {
        surveyKnown.set(surveyKey(r.x, r.y), r.status);
        // A cell the crew just finished: pull its tiles again, under a fresh cache version.
        const was = prev.get(surveyKey(r.x, r.y));
        if (r.status === 'done' && (was === 'queued' || was === 'fetching')) {
          const b = tileBoundsLonLat(14, r.x, r.y);
          world.invalidateRegion(
            b.minLon, b.minLat, b.maxLon, b.maxLat,
            r.finishedAt ?? String(Date.now()),
          );
        }
      }

      // Cells the server has never heard of become requests, nearest first (already sorted).
      // Cells outside Australia are dropped HERE: the server would refuse them without
      // writing a row, and a row-less cell would be re-asked on every poll, forever.
      // A cell that burned its retry budget gets exactly one fresh ask per session — the
      // server re-queues it under the backoff rules, and the guard set stops a poll loop.
      const missing = cells.filter(([x, y]) => {
        const b = tileBoundsLonLat(14, x, y);
        if (!withinAustralia((b.minLon + b.maxLon) / 2, (b.minLat + b.maxLat) / 2)) return false;
        const key = surveyKey(x, y);
        const status = surveyKnown.get(key);
        if (status === undefined) return true;
        if (status === 'failed' && !surveyRetried.has(key)) {
          surveyRetried.add(key);
          return true;
        }
        return false;
      });
      if (missing.length > 0) {
        const res = await surveyRequest(missing, lon, lat);
        for (const [x, y] of missing) surveyKnown.set(surveyKey(x, y), 'queued');
        if (res.refused === 'storage') {
          toast('The world map has reached its storage budget — no new areas for now.');
        }
      }

      let pending = 0;
      for (const [x, y] of cells) {
        const s = surveyKnown.get(surveyKey(x, y));
        if (s === 'queued' || s === 'fetching') pending += 1;
      }
      emit('world:surveys', { pending, fresh: 0 });
      if (pending > 0 && lastPendingShown === 0) {
        toast('This area is un-surveyed — the crew is on it. Blocks appear as they finish.');
      }
      if (pending === 0 && lastPendingShown > 0) {
        toast('Survey complete — every block in view is on the map.');
      }
      lastPendingShown = pending;

      if (pending > 0) scheduleSurveyPoll(8000);
    } catch (err) {
      console.warn('[surveys] reconcile failed; will retry', err);
      scheduleSurveyPoll(15000);
    } finally {
      surveyBusy = false;
    }
  }

  on('world:viewcells', (v) => {
    surveyView = v;
    void reconcileSurveys();
  });

  // The globe's gold dots: fetch where the crew has been each time the planet comes up.
  on('world:globe', ({ on: globeOn }) => {
    if (!globeOn) return;
    surveyMap()
      .then((points) => {
        emit('globe:dots', { points });
      })
      .catch((err: unknown) => {
        console.warn('[globe] survey map failed; dots skipped', err);
      });
  });
  window.__gfSurveys = () => {
    let pending = 0;
    for (const s of surveyKnown.values()) if (s === 'queued' || s === 'fetching') pending += 1;
    return { pending, fresh: 0, known: surveyKnown.size };
  };

  // Clicking the cart in the world opens its panel. The world emits, the UI listens — neither
  // imports the other (PLAN.md Part 2 §1).
  on('world:cartclick', () => {
    openCartWindow(windows);
  });

  /*
   * Clicking a building opens the inspector on it. The pick carries the OSM way id off the hit
   * triangle when it has one — so it resolves to the exact footprint — and the ground point as a
   * fallback for a triangle whose feature id did not survive. A click that hits no parcel (open
   * ground, a road) simply does nothing.
   */
  on('world:buildingclick', ({ osmWayId, lon, lat }) => {
    // Basemap buildings carry no way id, and even an id can predate its survey — so the
    // chain is: by id, then by position, then an honest "not on the books yet". The nearby
    // auto-survey is already queueing the cell whenever the camera lingers here.
    const lookup = (
      osmWayId !== null ? fetchParcelByOsmId(osmWayId) : Promise.resolve(null)
    ).then((byId) => byId ?? fetchParcelAt(lon, lat));
    void lookup
      .then((parcel) => {
        if (!parcel) {
          toast('This block is not surveyed yet — stay nearby and the crew will map it.');
          return;
        }
        openBuildingInspector(windows, root, parcel);
      })
      .catch((err: unknown) => {
        console.warn('[land] could not read the parcel', err);
        toast('Could not read that building.');
      });
  });

  /*
   * A player whose cart is still in the bag gets the placement flow instead of a panel. Driven
   * off the store rather than run once at boot, so it also covers the case where state arrives
   * late — and tears itself down the moment the cart becomes placed.
   *
   * Setting the cart down opens its panel, because the next decision — what it sells — is made
   * there. Only on that transition: a returning player with a cart already on the map gets the
   * map, not a window they did not ask for.
   */
  let placement: PlacementFlow | null = null;
  gameStore.select(
    (s) => (s?.cart ? s.cart.placed : null),
    (placed) => {
      if (placed === false && !placement) {
        placement = startPlacement(root, (mode) => {
          world.setPlacementMode(mode);
        });
      } else if (placed === true && placement) {
        placement.destroy();
        placement = null;
        openCartWindow(windows);
      }
    },
  );

  // The connection panel stays: it is how a Supabase outage becomes visible rather than
  // silently manifesting as a city that never loads.
  new ConnStatus().mount(hud);

  hud.appendChild(attribution());
  loading.mount(root);

  const devTools = IS_DEV || new URLSearchParams(window.location.search).has('debug');
  if (devTools) {
    installPerfOverlay(hud);

    /*
     * Dev cash faucet: press "$" to mint $1,000,000, so anything with a price can be tested
     * without grinding for it. Gated to dev/`?debug` builds — it calls a server function that
     * creates money from nothing, which never ships. Ignored while typing in a field, so it does
     * not fire mid-way through naming a cart. Goes when accounts land (see migration 0035).
     */
    window.addEventListener('keydown', (e) => {
      if (e.key !== '$') return;
      const focused = document.activeElement;
      if (focused instanceof HTMLInputElement || focused instanceof HTMLTextAreaElement) return;
      e.preventDefault();
      void devGrantCash(1000000)
        .then((state) => {
          incomeInterpolator.sync(state);
          gameStore.set(state);
          toast('+$1,000,000 — dev cash');
        })
        .catch((err: unknown) => {
          toast('Dev cash failed — is a session up yet?');
          console.warn('[dev] grant cash failed', err);
        });
    });
  }

  world.start();

  /*
   * Identity, then the heartbeat. Sign-in is anonymous and the player row is created by a
   * database trigger, so there is nothing to mint client-side (PLAN.md Part 3 §8).
   *
   * This is deliberately not awaited before the world starts: the map is public data and
   * renders without a session, so a slow or failed sign-in costs the player a HUD, not a city.
   * Without credentials at all the game still boots — the map is the part that works offline
   * from the economy's point of view.
   */
  const polling = new PollingScheduler({
    onError: (err) => {
      console.warn('[poll] game state failed', err);
    },
  });

  // See the Window augmentation in core/stats.ts for why exposing these is safe.
  window.__gfGameState = () => gameStore.get();
  window.__gfSetCartPrice = async (cartId, price) => {
    const state = await setCartPrice(cartId, price);
    incomeInterpolator.sync(state);
    gameStore.set(state);
    return state;
  };
  window.__gfBuy = async (itemId, qty, toCart) => {
    const state = await buyItem(itemId, qty, toCart);
    incomeInterpolator.sync(state);
    gameStore.set(state);
    return state;
  };
  window.__gfSell = async (itemId, qty, fromCart) => {
    const state = await sellItem(itemId, qty, fromCart);
    incomeInterpolator.sync(state);
    gameStore.set(state);
    return state;
  };
  window.__gfCheckPlacement = (lon, lat) => checkPlacement(lon, lat);
  window.__gfPlaceCart = async (lon, lat, cuisine) => {
    const cart = gameStore.get()?.cart;
    if (!cart) throw new Error('no cart');
    const state = await placeCart(cart.id, lon, lat, cuisine);
    incomeInterpolator.sync(state);
    gameStore.set(state);
    return state;
  };
  window.__gfSetCuisine = async (cuisine) => {
    const cart = gameStore.get()?.cart;
    if (!cart) throw new Error('no cart');
    const state = await setCartCuisine(cart.id, cuisine);
    incomeInterpolator.sync(state);
    gameStore.set(state);
    return state;
  };
  window.__gfPrep = async () => {
    const cart = gameStore.get()?.cart;
    if (!cart) throw new Error('no cart');
    const state = await prepCart(cart.id);
    incomeInterpolator.sync(state);
    gameStore.set(state);
    return state;
  };
  window.__gfRestock = async (cartId, servings) => {
    const state = await restockCart(cartId, servings);
    incomeInterpolator.sync(state);
    gameStore.set(state);
    return state;
  };
  window.__gfRestockQuote = (cartId, servings) => fetchRestockQuote(cartId, servings);

  // Land seams, for scripts/verify-land.mjs and the Phase 4 smoke test. Reads of the player's
  // own state and writes through the same RPCs the UI calls, which derive identity from the JWT.
  window.__gfAdvance = async (minutes) => {
    const state = await devAdvanceMinutes(minutes);
    incomeInterpolator.sync(state);
    gameStore.set(state);
    return state;
  };
  window.__gfGrant = async (amount) => {
    const state = await devGrantCash(amount);
    incomeInterpolator.sync(state);
    gameStore.set(state);
    return state;
  };
  window.__gfParcelAt = (lon, lat) => fetchParcelAt(lon, lat);
  window.__gfForSale = (lon, lat, maxPrice, limit) =>
    fetchParcelsForSale(lon, lat, maxPrice, 1500, limit ?? 12);
  // Open the inspector on a coordinate directly, for tests that should not depend on framing a
  // building under the cursor. The click path itself emits `world:buildingclick`, wired above.
  window.__gfOpenInspector = async (lon, lat) => {
    const parcel = await fetchParcelAt(lon, lat);
    if (parcel) openBuildingInspector(windows, root, parcel);
    return parcel;
  };
  window.__gfParcelByOsmId = (osmWayId) => fetchParcelByOsmId(osmWayId);
  window.__gfParcel = (parcelId) => fetchParcel(parcelId);
  window.__gfBuyParcel = async (parcelId) => {
    const state = await buyParcel(parcelId);
    incomeInterpolator.sync(state);
    gameStore.set(state);
    return state;
  };
  window.__gfSellParcel = async (parcelId) => {
    const state = await sellParcel(parcelId);
    incomeInterpolator.sync(state);
    gameStore.set(state);
    return state;
  };

  // The fit-out seams, for the retail-loop smoke test and scripts/verify-land.mjs. Same RPCs the
  // UI calls, same refresh path, so a scripted purchase updates the walked room too.
  window.__gfBuyFixture = async (parcelId, kind, floor = 0) => {
    applyState(await buyFixture(parcelId, kind, floor));
    return refreshInterior(parcelId);
  };
  window.__gfStockShelf = async (parcelId, fixtureId, itemId, price) => {
    applyState(await stockShelf(fixtureId, itemId, price));
    return refreshInterior(parcelId);
  };
  window.__gfOrderStock = async (parcelId, itemId, qty) => {
    applyState(await orderStock(parcelId, itemId, qty));
    return refreshInterior(parcelId);
  };
  window.__gfRemoveFixture = async (parcelId, fixtureId) => {
    applyState(await removeFixture(fixtureId));
    return refreshInterior(parcelId);
  };

  // Interior seams. Enter/switch/exit go through the very events the UI fires, and the interior
  // is pure client rendering of state the parcel read already returned.
  let lastInterior: InteriorState = { active: false };
  on('interior:state', (s) => {
    lastInterior = s;
  });
  window.__gfInteriorState = () => lastInterior;
  window.__gfEnterInterior = async (lon, lat) => {
    const parcel = await fetchParcelAt(lon, lat);
    if (parcel?.ownedByMe) emit('world:enterinterior', descriptorFrom(parcel));
    return parcel;
  };
  window.__gfSetFloor = (floor) => {
    emit('interior:setfloor', { floor });
  };
  window.__gfExitInterior = () => {
    emit('interior:exit');
  };

  void ensureSignedIn()
    .then(async () => {
      /*
       * Fresh start. Until there are accounts, an anonymous session silently resumes whatever
       * run it was in the middle of, which makes the opening minutes unreachable without
       * clearing site data by hand. A refresh is the reset. `?keep` opts out for one load.
       *
       * Failure here is not fatal: the run carries on as it was, which is exactly the state
       * the reset would have replaced.
       */
      if (RESET_ON_LOAD) {
        try {
          const fresh = await resetPlayer();
          incomeInterpolator.sync(fresh);
          gameStore.set(fresh);
        } catch (err) {
          console.warn('[boot] fresh-start reset failed; resuming the existing run', err);
        }
      }
      surveySessionReady = true;
      void reconcileSurveys();
      polling.start();
    })
    .catch((err: unknown) => {
      if (err instanceof NotConfiguredError) {
        console.warn('[boot] Supabase not configured; running map-only');
        return;
      }
      toast('Could not start your session. The map still works.');
      console.warn('[boot] sign-in failed', err);
    });

  // Failsafe: if no tile ever arrives — the endpoint is down, the area has no data — the
  // loading screen must still get out of the way so the failure is visible underneath it.
  window.setTimeout(() => {
    loading.finish();
  }, 12000);

  document.documentElement.dataset['gfBoot'] = 'ready';
}

main();
